import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Container, Chip, Button, Skeleton, Stack,
    Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, TextField,
    InputAdornment, IconButton, Tooltip, Divider
} from '@mui/material';
import DirectionsBikeIcon  from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon   from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon  from '@mui/icons-material/DirectionsWalk';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import PersonIcon          from '@mui/icons-material/Person';
import ReceiptIcon         from '@mui/icons-material/Receipt';
import SearchIcon          from '@mui/icons-material/Search';
import CloseIcon           from '@mui/icons-material/Close';
import EditIcon            from '@mui/icons-material/Edit';
import AssignmentIndIcon   from '@mui/icons-material/AssignmentInd';
import { useNavigate }     from 'react-router-dom';
import { useTranslation }  from 'react-i18next';
import axios               from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';

const TOUR_STATUSES = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const STATUS_CONFIG = {
    SCHEDULED:   { color: 'info',    label: 'Scheduled'   },
    IN_PROGRESS: { color: 'warning', label: 'In Progress' },
    COMPLETED:   { color: 'success', label: 'Completed'   },
    CANCELLED:   { color: 'error',   label: 'Cancelled'   },
};

const TYPE_ICONS = {
    WALK: <DirectionsWalkIcon sx={{ fontSize: '1.1rem' }} />,
    BIKE: <DirectionsBikeIcon sx={{ fontSize: '1.1rem' }} />,
    BUS:  <DirectionsBusIcon  sx={{ fontSize: '1.1rem' }} />,
};

function CardSkeleton() {
    return (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: '16px',
            border: '1px solid rgba(44,31,20,0.08)', mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Skeleton variant="text" width={200} height={26} />
                <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: '20px' }} />
            </Box>
            <Stack direction="row" spacing={3}>
                <Skeleton variant="text" width={120} height={18} />
                <Skeleton variant="text" width={100} height={18} />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: '20px' }} />
                <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: '20px' }} />
            </Box>
        </Box>
    );
}

export default function ToursPage() {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const { t }      = useTranslation();
    const isManager  = user?.role === 'MANAGER' || user?.role === 'ADMIN';
    const isGuide    = user?.role === 'GUIDE';

    const [tours,     setTours]     = useState([]);
    const [guides,    setGuides]    = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [search,    setSearch]    = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Guide assignment dialog
    const [assignDialog, setAssignDialog]   = useState(false);
    const [selectedTour, setSelectedTour]   = useState(null);
    const [selectedGuide, setSelectedGuide] = useState('');
    const [assigning,    setAssigning]      = useState(false);

    // Notes dialog
    const [notesDialog, setNotesDialog] = useState(false);
    const [notesValue,  setNotesValue]  = useState('');
    const [savingNotes, setSavingNotes] = useState(false);

    useEffect(() => {
        if (!user?.id) return;
        const url = isManager
            ? '/api/tours/view'
            : `/api/tours/view/guide/${user.id}`;
        axios.get(url)
            .then(res => setTours(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => setTours([]))
            .finally(() => setLoading(false));
    }, [isManager, user?.id]);

    useEffect(() => {
        if (!isManager) return;
        axios.get('/guides/view')
            .then(res => setGuides(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => {});
    }, [isManager]);

    const guideMap = useMemo(() => {
        const m = {};
        guides.forEach(g => { m[g.id] = g; });
        return m;
    }, [guides]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return tours.filter(t => {
            const matchSearch = !q ||
                (t.offerTitle || '').toLowerCase().includes(q) ||
                (t.type || '').toLowerCase().includes(q);
            const matchStatus = statusFilter === 'ALL' || t.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [tours, search, statusFilter]);

    const handleStatusChange = (tourId, newStatus) => {
        axios.put(`/api/tours/status/${tourId}?status=${newStatus}`)
            .then(res => setTours(prev => prev.map(t => t.id === tourId ? res.data : t)))
            .catch(err => console.error('Status update failed', err));
    };

    const openAssignDialog = (tour) => {
        setSelectedTour(tour);
        setSelectedGuide(tour.guideId || '');
        setAssignDialog(true);
    };

    const confirmAssign = () => {
        if (!selectedGuide || !selectedTour) return;
        setAssigning(true);
        axios.put(`/api/tours/assign-guide/${selectedTour.id}?guideId=${selectedGuide}`)
            .then(res => {
                setTours(prev => prev.map(t => t.id === selectedTour.id ? res.data : t));
                setAssignDialog(false);
            })
            .catch(err => console.error('Guide assignment failed', err))
            .finally(() => setAssigning(false));
    };

    const openNotesDialog = (tour) => {
        setSelectedTour(tour);
        setNotesValue(tour.notes || '');
        setNotesDialog(true);
    };

    const saveNotes = () => {
        setSavingNotes(true);
        axios.put(`/api/tours/notes/${selectedTour.id}`, notesValue, {
            headers: { 'Content-Type': 'text/plain' }
        })
            .then(res => {
                setTours(prev => prev.map(t => t.id === selectedTour.id ? res.data : t));
                setNotesDialog(false);
            })
            .catch(err => console.error('Notes save failed', err))
            .finally(() => setSavingNotes(false));
    };

    const counts = useMemo(() => {
        const c = { ALL: tours.length };
        TOUR_STATUSES.forEach(s => { c[s] = tours.filter(t => t.status === s).length; });
        return c;
    }, [tours]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.2em', fontWeight: 700 }}>
                    {isGuide ? 'My Schedule' : 'Staff Management'}
                </Typography>
                <Typography variant="h4" fontWeight={800}
                            sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mb: 0.5 }}>
                    {isGuide ? 'My Tours' : 'Tours Management'}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
                    {[
                        { label: 'Total', value: counts.ALL, color: 'rgba(255,255,255,0.85)' },
                        { label: 'Scheduled',   value: counts.SCHEDULED,   color: '#64b5f6' },
                        { label: 'In Progress', value: counts.IN_PROGRESS, color: GOLD },
                        { label: 'Completed',   value: counts.COMPLETED,   color: '#81c784' },
                        { label: 'Cancelled',   value: counts.CANCELLED,   color: '#e57373' },
                    ].map(({ label, value, color }) => (
                        <Box key={label} sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
                                {loading ? '—' : value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem' }}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>

            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 5 } }}>
                {/* ── Filters ─────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search by offer or type…"
                               value={search} onChange={e => setSearch(e.target.value)}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} /></InputAdornment>,
                                   endAdornment: search ? (
                                       <InputAdornment position="end">
                                           <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                                       </InputAdornment>
                                   ) : null,
                               }}
                               sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                        {['ALL', ...TOUR_STATUSES].map(s => (
                            <Chip key={s}
                                  label={s === 'ALL' ? `All (${counts.ALL})` : `${STATUS_CONFIG[s]?.label} (${counts[s]})`}
                                  onClick={() => setStatusFilter(s)}
                                  color={statusFilter === s ? (s === 'ALL' ? 'default' : STATUS_CONFIG[s]?.color) : 'default'}
                                  variant={statusFilter === s ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 700, cursor: 'pointer',
                                      ...(statusFilter === s && s === 'ALL' && {
                                          bgcolor: WALNUT, color: 'white'
                                      })
                                  }} />
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary', fontWeight: 600 }}>
                        {loading ? 'Loading…' : `${filtered.length} tour${filtered.length !== 1 ? 's' : ''}`}
                    </Typography>
                </Box>

                {/* ── Tour list ────────────────────────────────────────────── */}
                {loading
                    ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
                    : filtered.length === 0
                        ? (
                            <Box sx={{ py: 10, textAlign: 'center', bgcolor: 'white',
                                borderRadius: '20px', border: '2px dashed rgba(44,31,20,0.12)' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mb: 1 }}>
                                    No tours found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {statusFilter !== 'ALL' ? 'Try a different status filter.' : 'No tours have been created yet.'}
                                </Typography>
                            </Box>
                        )
                        : filtered.map(tour => {
                            const guide = guideMap[tour.guideId];
                            const statusCfg = STATUS_CONFIG[tour.status] || { color: 'default', label: tour.status };
                            return (
                                <Box key={tour.id} sx={{ bgcolor: 'white', borderRadius: '16px', mb: 2,
                                    border: '1px solid rgba(44,31,20,0.08)',
                                    boxShadow: '0 2px 8px rgba(44,31,20,0.05)',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': { boxShadow: '0 6px 20px rgba(44,31,20,0.1)' } }}>
                                    <Box sx={{ p: 2.5 }}>
                                        {/* Top row */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                <Box sx={{ color: TERRACOTTA, display: 'flex' }}>
                                                    {TYPE_ICONS[tour.type]}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="h6" fontWeight={800}
                                                                sx={{ fontFamily: "'Playfair Display', Georgia, serif",
                                                                    color: WALNUT, lineHeight: 1.25, fontSize: '1.05rem' }}>
                                                        {tour.offerTitle || 'Unnamed Tour'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {tour.type}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Chip label={statusCfg.label} color={statusCfg.color} size="small"
                                                  sx={{ fontWeight: 700, borderRadius: '20px' }} />
                                        </Box>

                                        {/* Meta row */}
                                        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
                                            {tour.time && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {new Date(tour.time).toLocaleString(undefined, {
                                                            dateStyle: 'medium', timeStyle: 'short'
                                                        })}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                {guide ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Avatar src={guide.imageUrl} sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                                                            {(guide.username || '?')[0].toUpperCase()}
                                                        </Avatar>
                                                        <Typography variant="body2" sx={{ color: FOREST, fontWeight: 600 }}>
                                                            {guide.username}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2"
                                                                sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                        Unassigned
                                                    </Typography>
                                                )}
                                            </Box>
                                            {tour.billId && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <ReceiptIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{ fontFamily: 'monospace' }}>
                                                        {tour.billId.slice(0, 8)}…
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>

                                        {/* Notes */}
                                        {tour.notes && (
                                            <Box sx={{ bgcolor: PARCHMENT, borderRadius: '10px', px: 1.5, py: 1, mb: 1.5,
                                                borderLeft: `3px solid ${GOLD}` }}>
                                                <Typography variant="body2" sx={{ color: WALNUT, fontSize: '0.82rem' }}>
                                                    {tour.notes}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Actions */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                                            {/* Guide note edit — available to both guide and manager */}
                                            <Button size="small" startIcon={<EditIcon />}
                                                    onClick={() => openNotesDialog(tour)}
                                                    sx={{ borderRadius: '20px', color: WALNUT, border: '1px solid rgba(44,31,20,0.2)',
                                                        fontSize: '0.75rem', fontWeight: 700,
                                                        '&:hover': { bgcolor: STONE } }}>
                                                {tour.notes ? 'Edit notes' : 'Add notes'}
                                            </Button>

                                            {isManager && (
                                                <>
                                                    <Button size="small" startIcon={<AssignmentIndIcon />}
                                                            onClick={() => openAssignDialog(tour)}
                                                            sx={{ borderRadius: '20px', color: FOREST,
                                                                border: '1px solid rgba(74,103,65,0.3)',
                                                                fontSize: '0.75rem', fontWeight: 700,
                                                                '&:hover': { bgcolor: 'rgba(74,103,65,0.06)' } }}>
                                                        {tour.guideId ? 'Reassign guide' : 'Assign guide'}
                                                    </Button>

                                                    <FormControl size="small" sx={{ minWidth: 160 }}>
                                                        <Select value={tour.status}
                                                                onChange={e => handleStatusChange(tour.id, e.target.value)}
                                                                sx={{ borderRadius: '20px', fontSize: '0.75rem',
                                                                    fontWeight: 700, height: 32,
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'rgba(196,98,45,0.3)'
                                                                    } }}>
                                                            {TOUR_STATUSES.map(s => (
                                                                <MenuItem key={s} value={s} sx={{ fontSize: '0.82rem' }}>
                                                                    {STATUS_CONFIG[s]?.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })
                }
            </Container>

            {/* ── Assign Guide Dialog ───────────────────────────────────── */}
            <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 800, color: WALNUT }}>
                    Assign Guide
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Tour: <strong>{selectedTour?.offerTitle}</strong>
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Select Guide</InputLabel>
                        <Select value={selectedGuide} onChange={e => setSelectedGuide(e.target.value)}
                                label="Select Guide">
                            {guides.map(g => (
                                <MenuItem key={g.id} value={g.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={g.imageUrl} sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                                            {(g.username || '?')[0].toUpperCase()}
                                        </Avatar>
                                        {g.username}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAssignDialog(false)} sx={{ borderRadius: '20px' }}>Cancel</Button>
                    <Button onClick={confirmAssign} disabled={!selectedGuide || assigning} variant="contained"
                            sx={{ borderRadius: '20px', bgcolor: FOREST, '&:hover': { bgcolor: '#3a5233' } }}>
                        {assigning ? 'Assigning…' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Notes Dialog ─────────────────────────────────────────── */}
            <Dialog open={notesDialog} onClose={() => setNotesDialog(false)} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 800, color: WALNUT }}>
                    {selectedTour?.notes ? 'Edit Notes' : 'Add Notes'}
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={4} label="Notes"
                               value={notesValue} onChange={e => setNotesValue(e.target.value)}
                               sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNotesDialog(false)} sx={{ borderRadius: '20px' }}>Cancel</Button>
                    <Button onClick={saveNotes} disabled={savingNotes} variant="contained"
                            sx={{ borderRadius: '20px', bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' } }}>
                        {savingNotes ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
